import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Check, ArrowLeft, ExternalLink } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';

// Platform detailed data matching Motion Blinds structure
const platformsData = {
  'apple-home': {
    name: 'Apple Home',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Apple__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Matter & Thread',
        content: 'Motionblinds motors with built-in Eve technology support Matter and Thread. This allows the motors to be controlled directly from Apple Home, without the need for a Motionblinds bridge. However, this does require installation of the right border router with Matter and Thread.',
        link: { text: 'Choose the right border router', url: 'https://www.evehome.com/en/hub' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      }
    ]
  },
  'google-home': {
    name: 'Google Home',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1558089687-f282ffcbc126?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Google__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Matter & Thread',
        content: 'Motionblinds motors with built-in Eve technology support Matter and Thread. This allows the motors to be controlled directly from Google Assistant, without the need for a Motionblinds bridge. However, this does require installation of the right border router with Matter and Thread.',
        link: { text: 'Choose the right border router', url: 'https://www.evehome.com/en/hub' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      },
      {
        title: 'Cloud connection and Wi-Fi bridges',
        content: 'Motionblinds motors with built-in 433MHz technology can be connected to Motionblinds Wi-Fi bridges (CMD-01 and CM-20) to be controlled through Google Home. In the Google Home app, connection can be made with Motionblinds, after which the motors can be controlled from Google Home through a cloud-to-cloud connection.',
        link: { text: 'Find out how it works', url: '/faq' },
        image: 'https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3?w=800&q=80'
      }
    ]
  },
  'amazon-alexa': {
    name: 'Amazon Alexa',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1543512214-318c7553f230?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Alexa__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Matter & Thread',
        content: 'Motionblinds motors with built-in Eve technology support Matter and Thread. This allows the motors to be controlled directly from Amazon Alexa, without the need for a Motionblinds bridge. However, this does require installation of the right border router with Matter and Thread.',
        link: { text: 'Choose the right border router', url: 'https://www.evehome.com/en/hub' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      },
      {
        title: 'Cloud connection and Wi-Fi bridges',
        content: 'Motionblinds motors with built-in 433MHz technology can be connected to Motionblinds Wi-Fi bridges to be controlled through Amazon Alexa. Enable the Motionblinds skill in the Alexa app and link your account to control your blinds with voice commands.',
        link: { text: 'Find out how it works', url: '/faq' },
        image: 'https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3?w=800&q=80'
      }
    ]
  },
  'smartthings': {
    name: 'SmartThings',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Smartthings__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Matter Support',
        content: 'Motionblinds motors with Matter support can be connected directly to Samsung SmartThings hubs. This enables seamless integration with the Samsung smart home ecosystem and powerful automation capabilities.',
        link: { text: 'Learn more about SmartThings', url: 'https://www.smartthings.com' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      }
    ]
  },
  'home-assistant': {
    name: 'Home Assistant',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Home-Assistent__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Official Integration',
        content: 'Motionblinds is the first official shading partner of Home Assistant. Our motors integrate natively with Home Assistant, providing full local control with complete privacy and no cloud dependency.',
        link: { text: 'Home Assistant Integration', url: 'https://www.home-assistant.io/integrations/motionblinds/' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      },
      {
        title: 'Multiple Connection Options',
        content: 'Connect via Wi-Fi bridge for 433MHz motors, or use Matter/Thread for direct local control. Home Assistant supports all Motionblinds connection methods for maximum flexibility.',
        link: { text: 'View documentation', url: 'https://www.home-assistant.io' },
        image: 'https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3?w=800&q=80'
      }
    ]
  },
  'ifttt': {
    name: 'IFTTT',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_IFTTT__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Endless Automation Possibilities',
        content: 'Connect Motionblinds to hundreds of other services through IFTTT. Create applets that trigger your blinds based on weather, location, calendar events, and much more.',
        link: { text: 'Explore IFTTT applets', url: 'https://ifttt.com' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      }
    ]
  },
  'homey-pro': {
    name: 'Homey',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Homey__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Homey Integration',
        content: 'Control your Motionblinds through Homey for unified smart home control. Create flows and automations that combine your blinds with all your other smart devices.',
        link: { text: 'Get the Homey app', url: 'https://homey.app' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      }
    ]
  },
  'knx': {
    name: 'KNX',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=85',
    badge: '/img/badges/Works-with-badges_Small_KNX__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Building Automation Standard',
        content: 'Motionblinds wired motors integrate with KNX building automation systems. Perfect for commercial buildings, offices, and high-end residential projects requiring professional-grade automation.',
        link: { text: 'Learn more about KNX', url: 'https://www.knx.org' },
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80'
      }
    ]
  },
  'control4': {
    name: 'Control4',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Control4__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Professional Home Automation',
        content: 'Integrate Motionblinds with Control4 systems for luxury home automation. Our certified driver enables seamless control of your motorized window treatments through the Control4 ecosystem.',
        link: { text: 'Find a Control4 dealer', url: 'https://www.control4.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'loxone': {
    name: 'Loxone',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Loxone__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Smart Building Solutions',
        content: 'Motionblinds integrates with Loxone smart building systems. Automate your blinds based on sunlight, temperature, and presence detection for optimal comfort and energy efficiency.',
        link: { text: 'Discover Loxone', url: 'https://www.loxone.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'abb': {
    name: 'ABB',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_ABB__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'ABB Smart Home',
        content: 'Connect Motionblinds to ABB smart home and building automation systems. Professional-grade integration for residential and commercial projects.',
        link: { text: 'Learn more about ABB', url: 'https://new.abb.com/buildings/smarter-home' },
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80'
      }
    ]
  },
  'busch-jaeger': {
    name: 'Busch-Jaeger',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Busch-Jaeger__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'German Engineering',
        content: 'Integrate Motionblinds with Busch-Jaeger smart home systems. Premium quality switches and controls combined with motorized window treatments.',
        link: { text: 'Discover Busch-Jaeger', url: 'https://www.busch-jaeger.de' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'elan': {
    name: 'ELAN',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Elan__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Luxury Home Control',
        content: 'ELAN integration brings Motionblinds into your luxury home automation system. Control your blinds alongside lighting, climate, and entertainment from a single interface.',
        link: { text: 'Explore ELAN', url: 'https://www.elanhomesystems.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'bond': {
    name: 'Bond',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Bond__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'RF Control Made Smart',
        content: 'Bond bridges the gap between your Motionblinds RF motors and your smart home. Control your blinds through the Bond app or integrate with other platforms.',
        link: { text: 'Get Bond', url: 'https://bondhome.io' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'mediola': {
    name: 'Mediola',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Mediola__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Universal Smart Home Gateway',
        content: 'Mediola connects Motionblinds with a wide range of smart home devices and protocols. Create powerful automations across different brands and technologies.',
        link: { text: 'Discover Mediola', url: 'https://www.mediola.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'smart-home-hornbach': {
    name: 'Smart Home by Hornbach',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badges_Small_Smart-Home__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Easy Smart Home Setup',
        content: 'Integrate Motionblinds with the Smart Home by Hornbach system. An accessible entry point into home automation with professional-grade motorized window treatments.',
        link: { text: 'Learn more', url: 'https://www.hornbach.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'klikaanklikuit': {
    name: 'KlikAanKlikUit',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badge_Small_Klikaan__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Simple Wireless Control',
        content: 'Connect Motionblinds to KlikAanKlikUit for easy wireless control of your motorized window treatments. A popular choice in the Netherlands and Belgium.',
        link: { text: 'Discover KlikAanKlikUit', url: 'https://www.klikaanklikuit.nl' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'rti': {
    name: 'RTI',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85',
    badge: '/img/badges/Works-with-badges_Small_RTI__ScaleMaxWidthWzMwMF0.jpg',
    sections: [
      {
        title: 'Professional Control Systems',
        content: 'RTI integration enables Motionblinds control through professional-grade remote control systems. Perfect for custom installations and luxury homes.',
        link: { text: 'Explore RTI', url: 'https://www.rticorp.com' },
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
      }
    ]
  },
  'matter': {
    name: 'Matter',
    subtitle: 'Works with Motionblinds',
    heroImage: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=85',
    badge: '/img/badges/matter_lkup_rgb_night-1__ScaleMaxWidthWzMwMF0.webp',
    sections: [
      {
        title: 'The Future of Smart Home',
        content: 'Matter is the new universal standard for smart home devices. Motionblinds motors with Matter support work seamlessly with Apple Home, Google Home, Amazon Alexa, Samsung SmartThings, and any other Matter-certified platform.',
        link: { text: 'Learn about Matter', url: 'https://csa-iot.org/all-solutions/matter/' },
        image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80'
      },
      {
        title: 'No Bridge Required',
        content: 'With Matter and Thread built into the motor, there\'s no need for additional bridges or hubs. Simply scan the QR code and your blinds connect directly to your smart home ecosystem.',
        link: { text: 'Shop Matter-enabled products', url: '/products' },
        image: 'https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3?w=800&q=80'
      }
    ]
  }
};

export const PlatformDetailPage = () => {
  const { platformId } = useParams();
  const platform = platformsData[platformId];

  if (!platform) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-3xl mb-4">Platform Not Found</h1>
            <p className="text-neutral-600 mb-6">The platform "{platformId}" is not available.</p>
            <Link to="/motorization/smart-connectivity/works-with-motionblinds">
              <Button>Back to Works with Motionblinds</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section - Clean like Motion Blinds */}
      <section className="bg-neutral-100 pt-32 pb-16" data-testid="platform-hero">
        <div className="container-xl">
          <Link 
            to="/motorization/smart-connectivity/works-with-motionblinds" 
            className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Works with Motionblinds
          </Link>
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-3xl"
          >
            <h1 className="text-neutral-900 text-5xl lg:text-6xl font-serif leading-tight mb-4">
              {platform.name}
            </h1>
            <p className="text-2xl text-neutral-500 font-light">
              {platform.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-[40vh] overflow-hidden">
        <img 
          src={platform.heroImage}
          alt={platform.name}
          className="w-full h-full object-cover"
        />
      </section>

      {/* Content Sections - Matching Motion Blinds structure */}
      {platform.sections.map((section, index) => (
        <section 
          key={index} 
          className={`section-padding ${index % 2 === 1 ? 'bg-neutral-50' : ''}`}
          data-testid={`platform-section-${index}`}
        >
          <div className="container-xl">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <h2 className="text-3xl font-serif mb-6">{section.title}</h2>
                <p className="text-neutral-600 text-lg leading-relaxed mb-6">
                  {section.content}
                </p>
                {section.link && (
                  <a 
                    href={section.link.url}
                    target={section.link.url.startsWith('http') ? '_blank' : '_self'}
                    rel={section.link.url.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium transition-colors"
                  >
                    {section.link.text}
                    {section.link.url.startsWith('http') ? (
                      <ExternalLink className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </a>
                )}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <img 
                  src={section.image}
                  alt={section.title}
                  className="w-full rounded-xl shadow-lg"
                />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="section-padding bg-neutral-100" data-testid="platform-cta">
        <div className="container-xl">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Where to buy Motionblinds?</h3>
              <p className="text-neutral-600 mb-6">Find a retailer or dealer near you.</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white">
                    Find your store
                  </Button>
                </Link>
                <Link to="/trade">
                  <Button variant="outline">
                    For professionals
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Support center | FAQ</h3>
              <p className="text-neutral-600 mb-6">Get answers to common questions.</p>
              <Link to="/faq">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white">
                  Find your answer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PlatformDetailPage;
